import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";
import styled from "styled-components";
import loadingIconUrl from "@/assets/loading/icon_loading.svg?url";

export const LoadingContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const FullPageLoadingContainer = styled(LoadingContainer)`
	box-sizing: border-box;
	width: 100%;
	min-height: 100dvh;
	padding-bottom: 10dvh;
`;

export const Spinner = styled.div`
	width: 48px;
	height: 48px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #000000;
	border-radius: 50%;
	animation: spin 1s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
`;

export const LoadingText = styled.span`
	margin-left: 12px;
	font-weight: 400;
	font-size: 15px;
	line-height: 25px;
	color: #000000;
`;

const Wrapper = styled.div<{ $borderRadius?: number | string }>`
	position: relative;
	width: 100%;
	border-radius: ${({ $borderRadius }) =>
		$borderRadius === undefined
			? undefined
			: typeof $borderRadius === "number"
				? `${$borderRadius}px`
				: $borderRadius};
	overflow: ${({ $borderRadius }) => ($borderRadius === undefined ? "visible" : "hidden")};
`;

const Content = styled.div<{ $isLoading: boolean }>`
	filter: ${({ $isLoading }) => ($isLoading ? "grayscale(1)" : "none")};
	opacity: ${({ $isLoading }) => ($isLoading ? 0.45 : 1)};
	pointer-events: ${({ $isLoading }) => ($isLoading ? "none" : "auto")};
	user-select: ${({ $isLoading }) => ($isLoading ? "none" : "auto")};
	transition:
		filter 160ms ease,
		opacity 160ms ease;
`;

const Overlay = styled.div<{ $backgroundColor?: string; $sticky?: boolean }>`
	position: absolute;
	inset: 0;
	z-index: 1;
	display: flex;
	align-items: ${({ $sticky }) => ($sticky ? "flex-start" : "center")};
	justify-content: center;
	background: ${({ $backgroundColor }) => $backgroundColor ?? "rgb(255 255 255 / 72%)"};
	cursor: progress;
`;

/** Keeps the indicator centred in the visible part of a scrolling parent instead of the whole content. */
const StickyIndicator = styled.div`
	position: sticky;
	/* A percentage "top" on a sticky element refers to the scroll container's height. */
	top: 50%;
	transform: translateY(-50%);
`;

const LoadingImage = styled.img<{ $width: number | string; $height?: number | string }>`
	display: block;
	width: ${({ $width }) => (typeof $width === "number" ? `${$width}px` : $width)};
	height: ${({ $height }) =>
		$height === undefined ? "auto" : typeof $height === "number" ? `${$height}px` : $height};
	max-width: 80%;
`;

export interface LoadingIndicatorProps {
	size?: number | string;
	width?: number | string;
	height?: number | string;
	className?: string;
	style?: CSSProperties;
}

export const LoadingIndicator = ({ size = 80, width, height, className, style }: LoadingIndicatorProps) => (
	<LoadingImage
		src={loadingIconUrl}
		$width={width ?? size}
		$height={height}
		className={className}
		style={{
			...style,
			width: width ?? size,
			height: height ?? "auto",
		}}
		alt=""
		aria-hidden="true"
	/>
);

const VisuallyHidden = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

// Matches RootLayout's background so the handoff to the app shell doesn't flash.
const FullPageLoaderContainer = styled(FullPageLoadingContainer)`
	background-color: #ebeefd;
`;

// App-level loader for auth checks and lazy route chunks. Mirrors the static
// loader in index.html so the handoff from HTML to React looks like one loader.
export const FullPageLoader = ({ label = "Loading" }: { label?: string }) => (
	<FullPageLoaderContainer role="status" aria-live="polite" data-full-page-loader="">
		<LoadingIndicator />
		<VisuallyHidden>{label}</VisuallyHidden>
	</FullPageLoaderContainer>
);

export interface LoadingWrapperProps {
	isLoading: boolean;
	children: ReactNode;
	label?: string;
	indicatorSize?: number | string;
	borderRadius?: number | string;
	className?: string;
	overlayColor?: string;
	/**
	 * For wrappers taller than the scrolling area they sit in: centre the indicator in the visible
	 * part of that scroller, so it stays on screen however far the content is scrolled.
	 */
	stickyIndicator?: boolean;
}

export const LoadingWrapper = ({
	isLoading,
	children,
	label = "Loading",
	indicatorSize = 80,
	borderRadius,
	className,
	overlayColor,
	stickyIndicator = false,
}: LoadingWrapperProps) => {
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const content = contentRef.current;

		if (!content) {
			return;
		}

		if (isLoading) {
			content.setAttribute("inert", "");
		} else {
			content.removeAttribute("inert");
		}

		return () => content.removeAttribute("inert");
	}, [isLoading]);

	return (
		<Wrapper className={className} aria-busy={isLoading} $borderRadius={borderRadius}>
			<Content ref={contentRef} $isLoading={isLoading} aria-hidden={isLoading || undefined}>
				{children}
			</Content>
			{isLoading && (
				<Overlay $backgroundColor={overlayColor} $sticky={stickyIndicator} role="status" aria-live="polite">
					{stickyIndicator ? (
						<StickyIndicator>
							<LoadingIndicator size={indicatorSize} />
						</StickyIndicator>
					) : (
						<LoadingIndicator size={indicatorSize} />
					)}
					<VisuallyHidden>{label}</VisuallyHidden>
				</Overlay>
			)}
		</Wrapper>
	);
};
